/*
 * Redux action creators live here. Read Redux docs for more information:
 * http://redux.js.org/docs/basics/Actions.html
 *
 * For actions that handle making requests to API services, return actions formatted for
 * the API middleware, which provide an array of 3 action types (initiate request, success, and
 * failure), along with request data such as endpoint. Before the success action
 * is invoked from the middleware, data will be normalized according to the schema provided.
 */

import { Schemas, API, Repo, PrivacyLevel, base64 } from "@thenounproject/lingo-core";

import * as actionTypes from "./actionTypes";

import _unique from "lodash/uniq";
import _sortBy from "lodash/sortBy";
import QueryString from "query-string";
import { showNotification } from "@actions/useShowNotification";

const { CALL_API } = API;

export function setLayoutConfig(layoutConfig: string) {
  return {
    type: actionTypes.SET_LAYOUT_CONFIG,
    layoutConfig: layoutConfig,
  };
}

export function saveNotificationSeen(notificationId, spaceId: number = null, seen = true) {
  return {
    type: actionTypes.SAVE_NOTIFICATION_SEEN_SUCCESS,
    notificationId,
    spaceId,
    value: seen,
  };
}

// A special user fetch that passes the query string
// for magic token support
export function fetchEmailPrefs() {
  return {
    [CALL_API]: {
      types: [
        actionTypes.FETCH_EMAIL_PREFS,
        actionTypes.FETCH_EMAIL_PREFS_SUCCESS,
        actionTypes.FETCH_EMAIL_PREFS_FAILURE,
      ],
      endpoint: `me/preview/${window.location.search}`,
      method: "GET",
      schema: Schemas.USER,
    },
  };
}

export function saveEmailPrefs(prefs) {
  return {
    [CALL_API]: {
      types: [
        actionTypes.SAVE_EMAIL_PREFS,
        actionTypes.SAVE_EMAIL_PREFS_SUCCESS,
        actionTypes.SAVE_EMAIL_PREFS_FAILURE,
      ],
      method: "PUT",
      endpoint: `me/email-prefs/${window.location.search}`,
      schema: Schemas.USER,
      data: prefs,
    },
    prefs: prefs,
  };
}

export function editUserAvatar(userId, file) {
  const formData = new FormData();
  if (file) {
    formData.append("avatar", file);
  } else {
    formData.append("reset", "true");
  }
  return {
    id: userId,
    [CALL_API]: {
      types: [
        actionTypes.EDIT_USER_AVATAR,
        actionTypes.EDIT_USER_AVATAR_SUCCESS,
        actionTypes.EDIT_USER_AVATAR_FAILURE,
      ],
      endpoint: "me/avatar/",
      method: "PUT",
      formData: formData,
      schema: Schemas.USER,
    },
  };
}

export function editUserProfile(userId, name, email) {
  return {
    userId: userId,
    name: name,
    email: email,
    [CALL_API]: {
      types: [
        actionTypes.EDIT_USER_PROFILE,
        actionTypes.EDIT_USER_PROFILE_SUCCESS,
        actionTypes.EDIT_USER_PROFILE_FAILURE,
      ],
      endpoint: "me/",
      method: "PUT",
      schema: Schemas.USER,
      data: { name, email },
    },
  };
}

export function changePassword(password, new_password) {
  return {
    [CALL_API]: {
      types: [
        actionTypes.CHANGE_PASSWORD,
        actionTypes.CHANGE_PASSWORD_SUCCESS,
        actionTypes.CHANGE_PASSWORD_FAILURE,
      ],
      endpoint: "me/password/",
      method: "PUT",
      data: { password, new_password },
    },
  };
}

export const fetchSpaceMembersById = (spaceId, userIds) => {
  const query = {
    user_ids: userIds,
    include: ["kits"],
  };
  const queryString = `?query=${base64.encode(query)}`;

  return {
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_MEMBERS_BY_ID,
        actionTypes.FETCH_SPACE_MEMBERS_BY_ID_SUCCESS,
        actionTypes.FETCH_SPACE_MEMBERS_BY_ID_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members${queryString}`,
      schema: Schemas.SPACE_MEMBER,
    },
  };
};

export const fetchSpaceMembers = (spaceId, role, sort, page = 0) => {
  const pageSize = 50,
    query = {
      role,
      sort,
      limit: pageSize,
      offset: pageSize * page,
      include: ["kits"],
    };
  const queryString = `?query=${base64.encode(query)}`;

  return {
    spaceId,
    page,
    role,
    sort,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_MEMBERS,
        actionTypes.FETCH_SPACE_MEMBERS_SUCCESS,
        actionTypes.FETCH_SPACE_MEMBERS_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members${queryString}`,
      page,
      schema: Schemas.SPACE_MEMBER,
    },
  };
};

export const fetchSpaceMemberCounts = spaceId => {
  return {
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_MEMBER_COUNTS,
        actionTypes.FETCH_SPACE_MEMBER_COUNTS_SUCCESS,
        actionTypes.FETCH_SPACE_MEMBER_COUNTS_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/meta`,
    },
  };
};

export const fetchSpaceInvitations = (spaceId, sort, page = 0) => {
  const pageSize = 50,
    queryString = QueryString.stringify({
      sort,
      limit: pageSize,
      offset: pageSize * page,
    });

  return {
    spaceId,
    page,
    role: "invite",
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_INVITATIONS,
        actionTypes.FETCH_SPACE_INVITATIONS_SUCCESS,
        actionTypes.FETCH_SPACE_INVITATIONS_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/invitations?${queryString}`,
      page,
      schema: Schemas.INVITATION,
    },
  };
};

export const fetchSpaceTransferCandidates = spaceId => {
  const pageSize = 2000,
    role = "admin",
    sort = "alpha",
    query = {
      role,
      sort,
      limit: pageSize,
    };
  const queryString = `?query=${base64.encode(query)}`;

  return {
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_TRANSFER_CANDIDATES,
        actionTypes.FETCH_SPACE_TRANSFER_CANDIDATES_SUCCESS,
        actionTypes.FETCH_SPACE_TRANSFER_CANDIDATES_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members${queryString}`,
      schema: Schemas.SPACE_MEMBER,
    },
  };
};

export const fetchSpaceKits = (spaceId: number): any => ({
  id: spaceId,
  [CALL_API]: {
    types: [
      actionTypes.FETCH_SPACE_KITS,
      actionTypes.FETCH_SPACE_KITS_SUCCESS,
      actionTypes.FETCH_SPACE_KITS_FAILURE,
    ],
    endpoint: `spaces/${spaceId}/?include=kits`,
    schema: Schemas.SPACE,
  },
});

export function fetchSpacePreview(identifier: string | number) {
  return {
    identifier,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SPACE_PREVIEW,
        actionTypes.FETCH_SPACE_PREVIEW_SUCCESS,
        actionTypes.FETCH_SPACE_PREVIEW_FAILURE,
      ],
      endpoint: `spaces/${identifier}/preview`,
      schema: Schemas.SPACE_PREVIEW,
      method: "GET",
    },
  };
}

export function fetchKit(uuid, spaceId) {
  const queryString = QueryString.stringify({
    include: "space",
  });

  return {
    uuid: uuid,
    [CALL_API]: {
      types: [actionTypes.FETCH_KIT, actionTypes.FETCH_KIT_SUCCESS, actionTypes.FETCH_KIT_FAILURE],
      endpoint: `kits/${spaceId}/${uuid}?${queryString}`,
      schema: Schemas.KIT,
    },
  };
}

export function fetchKitOutline(spaceId, uuid, version) {
  return {
    uuid: uuid,
    version: version,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_KIT_OUTLINE,
        actionTypes.FETCH_KIT_OUTLINE_SUCCESS,
        actionTypes.FETCH_KIT_OUTLINE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/kits/${uuid}/outline?${QueryString.stringify({ v: version })}`,
      schema: Schemas.KIT_VERSION,
    },
  };
}

export function fetchSection(spaceId, uuid, version, page = 1) {
  const queryString = QueryString.stringify({
    limit: 96,
    v: version,
    page: page,
  });

  return {
    uuidv: `${uuid}-${version}`,
    uuid: uuid,
    page,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_SECTION,
        actionTypes.FETCH_SECTION_SUCCESS,
        actionTypes.FETCH_SECTION_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/sections/${uuid}?${queryString}`,
      page: page,
      schema: Schemas.SECTION,
    },
  };
}

// closeAll defaults to true to maintain current behavior
// We may want to switch that after we have a better understanding
// of where this will come into play
export function showModal(component, props = null, closeAll = true) {
  return {
    type: actionTypes.SHOW_MODAL,
    component: component,
    props: props,
    closeAll,
  };
}

export function dismissModal(e = null) {
  if (e) e.preventDefault();

  return {
    type: actionTypes.DISMISS_MODAL,
  };
}

export function fetchKitVersion(kitUuid, spaceId, version) {
  const queryString = QueryString.stringify({
    v: version,
  });
  return {
    uuid: kitUuid,
    version: version,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_KIT_VERSION,
        actionTypes.FETCH_KIT_VERSION_SUCCESS,
        actionTypes.FETCH_KIT_VERSION_FAILURE,
      ],
      endpoint: `kits/${kitUuid}?${queryString}`,
      schema: Schemas.KIT_VERSION,
      method: "GET",
    },
  };
}

export function fetchKitVersionHistory(spaceId, kitUuid) {
  return {
    uuid: kitUuid,
    [CALL_API]: {
      types: [
        actionTypes.FETCH_KIT_VERSION_HISTORY,
        actionTypes.FETCH_KIT_VERSION_HISTORY_SUCCESS,
        actionTypes.FETCH_KIT_VERSION_HISTORY_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitUuid}/version-history`,
      schema: Schemas.KIT,
      method: "GET",
    },
  };
}

export const duplicateKit = (kitId, spaceId) => dispatch => {
  dispatch(
    showNotification({
      message:
        "Your kit is being duplicated. It may take up to a few minutes. We'll let you know when it is ready.",
    })
  );
  return dispatch({
    sourceId: kitId,
    spaceId: spaceId,
    [CALL_API]: {
      types: [
        actionTypes.DUPLICATE_KIT,
        actionTypes.DUPLICATE_KIT_SUCCESS,
        actionTypes.DUPLICATE_KIT_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitId}/duplicate`,
      schema: Schemas.KIT_VERSION,
      method: "POST",
    },
  }).then(result => {
    if (result.error) {
      dispatch(
        showNotification({
          message: `There was a problem duplicating your kit ${result.error.message}`,
          level: "error",
        })
      );
    }
    // The polling middleware will poll for the new kit and show a notification when it is ready
    return result;
  });
};

export function pollForDuplicateKit(kitId, sourceId, spaceId) {
  return {
    kitId,
    sourceId,
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.POLL_FOR_DUPLICATE_KIT,
        actionTypes.POLL_FOR_DUPLICATE_KIT_SUCCESS,
        actionTypes.POLL_FOR_DUPLICATE_KIT_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitId}?`,
      schema: Schemas.KIT,
    },
  };
}

// Updates version being used by this user
export function setKitUseVersion(spaceId, kitUuid, userId, version) {
  return {
    spaceId: spaceId,
    kitUuid: kitUuid,
    version: version,
    [CALL_API]: {
      types: [
        actionTypes.SET_KIT_USE_VERSION,
        actionTypes.SET_KIT_USE_VERSION_SUCCESS,
        actionTypes.SET_KIT_USE_VERSION_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitUuid}/members/${userId}`,
      method: "PUT",
      data: {
        use_version: version,
      },
    },
  };
}

export function setKitFavorite(spaceId, kitUuid, userId, isFavorite) {
  return {
    spaceId: spaceId,
    kitUuid: kitUuid,
    isFavorite: isFavorite,
    [CALL_API]: {
      types: [
        actionTypes.SET_KIT_FAVORITE,
        actionTypes.SET_KIT_FAVORITE_SUCCESS,
        actionTypes.SET_KIT_FAVORITE_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitUuid}/members/${userId}`,
      method: "PUT",
      schema: Schemas.KIT_MEMBER,
      data: {
        is_favorite: isFavorite,
      },
    },
  };
}

export function deleteKitVersion(kitUuid, spaceId, version) {
  return {
    uuid: kitUuid,
    spaceId: spaceId,
    version: version,
    [CALL_API]: {
      types: [
        actionTypes.DELETE_KIT_VERSION,
        actionTypes.DELETE_KIT_VERSION_SUCCESS,
        actionTypes.DELETE_KIT_VERSION_FAILURE,
      ],
      endpoint: `kits/${kitUuid}`,
      schema: Schemas.KIT_VERSION,
      method: "PUT",
      data: {
        version: version,
        status: "deleted",
      },
    },
  };
}

export function editKitVersion(kitUuid, version, name, notes) {
  return {
    name: name,
    notes: notes,
    uuid: kitUuid,
    [CALL_API]: {
      types: [
        actionTypes.EDIT_KIT_VERSION,
        actionTypes.EDIT_KIT_VERSION_SUCCESS,
        actionTypes.EDIT_KIT_VERSION_FAILURE,
      ],
      endpoint: `kits/${kitUuid}`,
      method: "PUT",
      schema: Schemas.KIT_VERSION,
      data: {
        version: version,
        version_identifier: name,
        notes: notes,
      },
    },
  };
}

export function createKit(spaceId, name, templateName) {
  const template_id = {
    styleGuide: "brand_style_guide",
    uiLibrary: "ui_component_library",
    assetLibrary: "digital_asset_library",
  }[templateName];
  return {
    spaceId: spaceId,
    template: templateName || undefined,
    [CALL_API]: {
      types: [
        actionTypes.CREATE_KIT,
        actionTypes.CREATE_KIT_SUCCESS,
        actionTypes.CREATE_KIT_FAILURE,
      ],
      endpoint: "kits",
      method: "POST",
      data: {
        space_id: spaceId,
        name,
        template_id,
      },
      schema: Schemas.KIT,
    },
  };
}

export function editKit(uuid, spaceId, name, description, coverImage, removeCoverImage) {
  let formData,
    data = {
      name,
      description,
      cover_reset: false,
    };
  if (removeCoverImage) {
    data.cover_reset = true;
  }
  if (coverImage) {
    formData = new FormData();
    formData.append("cover", coverImage);
    formData.append("json", JSON.stringify(data));
    data = undefined;
  }
  return {
    uuid: uuid,
    spaceId: spaceId,
    [CALL_API]: {
      types: [actionTypes.EDIT_KIT, actionTypes.EDIT_KIT_SUCCESS, actionTypes.EDIT_KIT_FAILURE],
      endpoint: `kits/${spaceId}/${uuid}`,
      method: "PUT",
      formData,
      data,
      schema: Schemas.KIT,
    },
  };
}

export function createSection(kitId, name): any {
  return {
    kitId: kitId,
    [CALL_API]: {
      types: [
        actionTypes.CREATE_SECTION,
        actionTypes.CREATE_SECTION_SUCCESS,
        actionTypes.CREATE_SECTION_FAILURE,
      ],
      endpoint: "sections",
      method: "POST",
      data: {
        kit_uuid: kitId,
        name: name,
      },
      schema: Schemas.SECTION,
    },
  };
}

/**
 * Batch updates an array of sections
 * @param {String} kitId Kit id
 * @param {Array} data [{uuid, key}]
 */
export function batchUpdateSections(data): any {
  return {
    [CALL_API]: {
      types: [
        actionTypes.BATCH_UPDATE_SECTIONS,
        actionTypes.BATCH_UPDATE_SECTIONS_SUCCESS,
        actionTypes.BATCH_UPDATE_SECTIONS_FAILURE,
      ],
      endpoint: `sync/sections`,
      method: "POST",
      data: { sections: data },
      schema: Schemas.SECTION,
    },
  };
}

export function updateSection(sectionId, data): any {
  return {
    sectionId,
    [CALL_API]: {
      types: [
        actionTypes.UPDATE_SECTION,
        actionTypes.UPDATE_SECTION_SUCCESS,
        actionTypes.UPDATE_SECTION_FAILURE,
      ],
      endpoint: `sections/${sectionId}`,
      method: "PUT",
      data,
      schema: Schemas.SECTION,
    },
  };
}

export function deleteSection(sectionId): any {
  return {
    sectionId,
    [CALL_API]: {
      types: [
        actionTypes.DELETE_SECTION,
        actionTypes.DELETE_SECTION_SUCCESS,
        actionTypes.DELETE_SECTION_FAILURE,
      ],
      endpoint: `sections/${sectionId}`,
      method: "PUT",
      data: {
        status: "deleted",
      },
      schema: Schemas.SECTION,
    },
  };
}

export const sortSectionItems = sectionIds => (dispatch, getState) => {
  const repo = new Repo(getState());
  return dispatch({
    type: actionTypes.SORT_SECTION_ITEMS,
    sections: sectionIds.reduce((acc, sId) => {
      acc[`${sId}-0`] = {
        items: _sortBy(repo.getSection(sId, 0).items, [item => repo.getItem(item, 0).displayOrder]),
      };
      return acc;
    }, {}),
  });
};

export function editKitPrivacy(
  kitId: string,
  spaceId: string | number,
  privacyLevel: PrivacyLevel,
  password: string = undefined
) {
  const data: { privacy: string; password?: string } = {
    privacy: privacyLevel,
  };
  switch (privacyLevel) {
    case PrivacyLevel.password: {
      if (!password) {
        return { type: actionTypes.EDIT_KIT_PRIVACY_FAILURE };
      }
      data.password = password;
      break;
    }
    case PrivacyLevel.private:
    case PrivacyLevel.public:
      break;
    default:
      return { type: actionTypes.EDIT_KIT_PRIVACY_FAILURE };
  }

  return {
    [CALL_API]: {
      types: [
        actionTypes.EDIT_KIT_PRIVACY,
        actionTypes.EDIT_KIT_PRIVACY_SUCCESS,
        actionTypes.EDIT_KIT_PRIVACY_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitId}`,
      schema: Schemas.KIT,
      method: "PUT",
      data,
    },
  };
}

export function editKitPortalAccess(kitId, spaceId, portalAccess) {
  return {
    [CALL_API]: {
      types: [
        actionTypes.EDIT_KIT_PORTAL_ACCESS,
        actionTypes.EDIT_KIT_PORTAL_ACCESS_SUCCESS,
        actionTypes.EDIT_KIT_PORTAL_ACCESS_FAILURE,
      ],
      endpoint: `kits/${spaceId}/${kitId}`,
      schema: Schemas.KIT,
      method: "PUT",
      data: { portal_access: Boolean(portalAccess) },
    },
  };
}

export const sendInvitations = (spaceId, emails, role, kitRoles, note) => (dispatch, getState) => {
  const getKitList = (allKits: string[]) => {
    if (role === "admin") return [];

    return _unique(allKits)
      .filter(k => role !== "limited_member" || kitRoles[k])
      .map(k => {
        return {
          uuid: k,
          role: kitRoles[k] || "guest",
        };
      });
  };

  const space = getState().entities.spaces[spaceId],
    spotsTaken = space.counts.users + space.counts.invitations,
    spotsAvailable = space.maxUsers === null ? Infinity : space.maxUsers - spotsTaken,
    kits = getKitList(space.kits),
    invitations = emails
      .filter(e => e.valid)
      .map(e => {
        return {
          email: e.email,
          space_id: spaceId,
          join_with: {
            role,
            kits,
          },
          note,
        };
      })
      .slice(0, spotsAvailable);

  return dispatch({
    spaceId: spaceId,
    [CALL_API]: {
      types: [
        actionTypes.INVITE_MEMBERS,
        actionTypes.INVITE_MEMBERS_SUCCESS,
        actionTypes.INVITE_MEMBERS_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/invite`,
      method: "POST",
      schema: Schemas.INVITATION,
      data: {
        invitations,
      },
    },
  });
};

export function deleteInvitations(invitationIds, spaceId) {
  const data = { invitations: invitationIds.map(id => ({ invitation_id: id })) };
  return {
    invitationIds,
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.DELETE_INVITATION,
        actionTypes.DELETE_INVITATION_SUCCESS,
        actionTypes.DELETE_INVITATION_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/invite`,
      method: "DELETE",
      schema: Schemas.INVITATION,
      data,
    },
  };
}

export const changeMultipleSpaceMemberRoles = (userIds, spaceId, role) => (dispatch, getState) => {
  const repo = new Repo(getState());
  // Previous roles are used to decrement counts in the reducer
  const prevRoles = userIds.reduce((acc, userId) => {
    acc[`${spaceId}-${userId}`] = repo.getSpaceMember(spaceId, userId)?.role;
    return acc;
  }, {});
  const queryString = `?query=${base64.encode({ include: ["kits"] })}`;

  return dispatch({
    spaceId,
    userIds,
    role,
    prevRoles,
    [CALL_API]: {
      types: [
        actionTypes.BATCH_CHANGE_SPACE_MEMBER_ROLE,
        actionTypes.BATCH_CHANGE_SPACE_MEMBER_ROLE_SUCCESS,
        actionTypes.BATCH_CHANGE_SPACE_MEMBER_ROLE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members${queryString}`,
      method: "PUT",
      schema: Schemas.SPACE_MEMBER,
      data: {
        members: userIds.map(id => ({ user_id: id, role })),
      },
    },
  });
};

export const changeSpaceMemberRole = (userId, spaceId, role) => (dispatch, getState) => {
  // Setting previous role for optimistic update rollback in case of failure
  const repo = new Repo(getState());
  const prevRole = repo.getSpaceMember(spaceId, userId).role;

  return dispatch({
    spaceId: spaceId,
    userId: userId,
    role: role,
    prevRole: prevRole,
    [CALL_API]: {
      types: [
        actionTypes.CHANGE_SPACE_MEMBER_ROLE,
        actionTypes.CHANGE_SPACE_MEMBER_ROLE_SUCCESS,
        actionTypes.CHANGE_SPACE_MEMBER_ROLE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members/${userId}?include=kits`,
      method: "PUT",
      schema: Schemas.SPACE_MEMBER,
      data: { role: role },
    },
  });
};

export function deleteSpaceMembers(userIds, spaceId) {
  return {
    spaceId,
    userIds,
    [CALL_API]: {
      types: [
        actionTypes.DELETE_SPACE_MEMBERS,
        actionTypes.DELETE_SPACE_MEMBERS_SUCCESS,
        actionTypes.DELETE_SPACE_MEMBERS_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members`,
      method: "PUT",
      schema: Schemas.SPACE_MEMBER,
      data: {
        members: userIds.map(id => ({ user_id: id, status: "revoked" })),
      },
    },
  };
}

export function leaveSpace(spaceId, userId) {
  return {
    spaceId,
    [CALL_API]: {
      types: [
        actionTypes.LEAVE_SPACE,
        actionTypes.LEAVE_SPACE_SUCCESS,
        actionTypes.LEAVE_SPACE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/members/${userId}`,
      method: "DELETE",
      schema: Schemas.SPACE_MEMBER,
    },
  };
}

export const changeMultipleKitMemberRoles =
  (userIds, spaceId, kitIds, role) => (dispatch, getState) => {
    const repo = new Repo(getState());
    const kits = kitIds.reduce((acc, uuid) => {
        acc[uuid] = repo.getKit(uuid, spaceId);
        return acc;
      }, {}),
      data = {
        members: kitIds
          .map(kitId =>
            userIds.map(userId => ({
              user_id: userId,
              kit_uuid: kitId,
              space_id: spaceId,
              ...(role === "revoked"
                ? { status: "revoked" }
                : kits[kitId]?.purchase
                ? { status: "active" }
                : { role, status: "active" }),
            }))
          )
          .flat(),
      };

    return dispatch({
      kitIds,
      spaceId,
      userIds,
      role,
      [CALL_API]: {
        types: [
          actionTypes.BATCH_CHANGE_KIT_MEMBER_ROLE,
          actionTypes.BATCH_CHANGE_KIT_MEMBER_ROLE_SUCCESS,
          actionTypes.BATCH_CHANGE_KIT_MEMBER_ROLE_FAILURE,
        ],
        endpoint: `sync/kits/members`,
        method: "POST",
        schema: Schemas.KIT_MEMBER,
        data,
      },
    });
  };

export function transferSpace(spaceId, userId) {
  return {
    spaceId: spaceId,
    newOwnerId: userId,
    [CALL_API]: {
      types: [
        actionTypes.TRANSFER_SPACE,
        actionTypes.TRANSFER_SPACE_SUCCESS,
        actionTypes.TRANSFER_SPACE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/transfer`,
      schema: Schemas.SPACE,
      method: "POST",
      data: {
        user_id: parseInt(userId),
      },
    },
  };
}

export function deleteSpace(spaceId) {
  return {
    id: spaceId,
    [CALL_API]: {
      types: [
        actionTypes.DELETE_SPACE,
        actionTypes.DELETE_SPACE_SUCCESS,
        actionTypes.DELETE_SPACE_FAILURE,
      ],
      endpoint: `spaces/${spaceId}/`,
      schema: Schemas.SPACE,
      method: "DELETE",
      data: {},
    },
  };
}

export function dismissUploads() {
  return {
    type: actionTypes.UPLOADS_DISMISS,
  };
}

export function downloadFile(url) {
  return {
    type: actionTypes.DOWNLOAD_FILE,
    url: url,
  };
}

export function changeInspectorMode(mode) {
  localStorage.setItem("lingo_inspector_preference", mode);
  return {
    type: actionTypes.CHANGE_INSPECTOR_VIEW_MODE,
    mode,
  };
}

export function changeGalleryAssetViewMode(mode) {
  localStorage.setItem("lingo_gallery_asset_view_preference", mode);
  return {
    type: actionTypes.CHANGE_GALLERY_ASSET_VIEW_MODE,
    mode,
  };
}
